﻿import React, { Suspense } from "react";
import ReactDOM from "react-dom/client";
import { getMissingEnvVars } from "./utils/validateEnv";
import { store } from "./redux/store";
import { Provider } from "react-redux";
import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import "@fontsource-variable/plus-jakarta-sans";
import "@fontsource-variable/rubik";
import "@fontsource-variable/montserrat";
import "intro.js/introjs.css";
import ReactLoading from "react-loading";
import {
  BrowserRouter,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router-dom";
import * as Sentry from "@sentry/react";
import App from "./App";
import "./index.css";
import NotFound from "./pages/notFound/NotFound";
import { CustomThemeProvider } from "./context/ThemeContext";
Sentry.init({
  // H6 OWASP A05 — DSN cargado desde variable de entorno, no hardcodeado en el código fuente
  dsn: import.meta.env.VITE_SENTRY_DSN,
  // Adds request headers and IP for users, for more info visit:
  // https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/#sendDefaultPii

  // Enable logs to be sent to Sentry
  _experiments: { enableLogs: true },

  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.

  integrations: [
    // Keep the Replay integration as before
    Sentry.replayIntegration({
      // Additional SDK configuration goes in here, for example:
      maskAllText: true,
      // H8 OWASP A02 — Enmascarar inputs en grabaciones de sesión para evitar
      // que credenciales o datos sensibles queden registrados en Sentry Replay
      maskAllInputs: true,
      blockAllMedia: true,
    }),
    // The following is all you need to enable performance monitoring with React Router v6
    // This will automatically instrument all routes and transactions
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    // The following is all you need to enable canvas recording with Replay
    Sentry.replayCanvasIntegration(),

    // Con esta integración, se pueden enviar eventos de feedback a Sentry
    // para que los usuarios puedan reportar errores o problemas en la aplicación.
    /*
    Sentry.feedbackIntegration({
      // Additional SDK configuration goes in here, for example:
      colorScheme: "system",
    }),
    */
  ],

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 0.1,
  // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ["localhost", "https://vobackrena.arsrenacer.com/"],

  //profilesSampleRate: 1.0,
});

//const persistor = persistStore(store);

const missingVars = getMissingEnvVars();
if (missingVars.length > 0) {
  const container = document.getElementById("root");
  container.innerHTML = `
    <div style="
      display:flex;flex-direction:column;align-items:center;justify-content:center;
      min-height:100vh;padding:2rem;font-family:monospace;background:#0d1117;color:#e6edf3;
    ">
      <div style="
        background:#161b22;border:1px solid #f85149;border-radius:12px;
        padding:2rem 2.5rem;max-width:560px;width:100%;
      ">
        <div style="font-size:2rem;margin-bottom:1rem">⚙️</div>
        <h2 style="margin:0 0 .5rem;color:#f85149;font-size:1.2rem">
          Variables de entorno faltantes
        </h2>
        <p style="margin:0 0 1rem;color:#8b949e;font-size:.85rem">
          La aplicación no puede arrancar. Agrega las siguientes variables
          a tu archivo <code style="color:#79c0ff">.env</code>:
        </p>
        <ul style="margin:0;padding-left:1.2rem;line-height:2">
          ${missingVars.map((v) => `<li style="color:#ffa657">${v}</li>`).join("")}
        </ul>
        <p style="margin:1rem 0 0;color:#8b949e;font-size:.8rem">
          Reinicia el servidor de desarrollo tras agregar las variables.
        </p>
      </div>
    </div>`;
  throw new Error(`Missing env vars: ${missingVars.join(", ")}`);
}

const container = document.getElementById("root");
const root = ReactDOM.createRoot(container, {
  // Callback called when React catches an error in an ErrorBoundary.
  onCaughtError: Sentry.reactErrorHandler(),
  // Callback called when React automatically recovers from errors.
  onRecoverableError: Sentry.reactErrorHandler(),
});

root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<NotFound />}>
      <Suspense
        fallback={
          <>
            <ReactLoading
              type="spokes"
              color="#0000FF"
              height={100}
              width={50}
            />
          </>
        }
      >
        <Provider store={store}>
          <BrowserRouter>
            <CustomThemeProvider>
              <App />
            </CustomThemeProvider>
          </BrowserRouter>
        </Provider>
      </Suspense>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);
